import { createFileRoute } from '@tanstack/react-router';
import { Container, SimpleGrid, Stack, Text } from '@mantine/core';
import { useAuth } from '../../auth/AuthContext';
import { PageHeader } from '../../components/PageHeader';
import { SiteStatusStrip } from '../../components/SiteStatusStrip';
import { TASK_CARD_STYLES, TaskButton } from '../../components/TaskButton';
import { HOME_TASKS } from '../../lib/taskButtons';
import { useTenantFeatures } from '../../lib/tenantFeatures';

/** Role-aware home (BUILD_SPEC.md §11): big task buttons filtered to the active
 * tenant's role, plus the site-status strip.
 *
 * Design-uplift pass (2026-08-25 — Stuart: "Welcome, Stuart@zebrarenewables.com"
 * as an H1 "looks basic"): the raw-email greeting, flat stat row and sparse task
 * cards are replaced below; see PageHeader/SiteStatusStrip/TaskButton for the
 * per-piece reasoning.
 */
export const Route = createFileRoute('/_authenticated/')({
  component: HomePage,
});

/** Icon-chip color per task family (TaskButton's `color` prop) — keyed by label
 * since lib/taskButtons.ts's HomeTask type was only extended with `description`
 * (this goal's file-scope for that file). A task this map doesn't know about
 * just falls back to TaskButton's own default (graphite) rather than crashing,
 * so a future task added without updating this list still renders correctly. */
// Round-2 QA fix (2026-08-25): the first pass paired 'blue'/'indigo' on
// Receive/Dispatch — both render as near-identical light-blue chips behind
// near-identical truck icons at this size, defeating the "scannable at a
// glance" point of per-family color. Spread further apart round the wheel.
const TASK_COLORS: Record<string, string> = {
  'Receive a load': 'blue',
  'Record a shift': 'grape',
  'Silo reading': 'teal',
  'Dispatch a load': 'pink',
  'Raise invoices': 'indigo',
  'Issue stores': 'cyan',
};

function greetingForHour(hour: number): string {
  if (hour < 5) return 'Good evening';
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/** First name, Title Case, from the email local-part (e.g. "stuart.fitzgerald@…"
 * -> "Stuart") — never the raw email as a page title (Stuart's complaint above).
 * The full email is still one click away in the user menu (Shell.tsx's
 * UserMenu), so nothing is lost, just relocated to where it doesn't crowd the
 * headline. */
function displayNameFromEmail(email: string | null): string {
  if (!email) return '';
  const local = email.split('@')[0] ?? '';
  const first = (local.split(/[._-]/)[0] ?? local).trim();
  return first ? first.charAt(0).toUpperCase() + first.slice(1).toLowerCase() : '';
}

function HomePage() {
  const auth = useAuth();
  const role = auth.activeTenant?.role ?? null;
  const features = useTenantFeatures();
  const tasks = HOME_TASKS.filter(
    (task) => (!role || task.roles.includes(role)) && (!task.requiresSilos || features.silosEnabled),
  );
  const name = displayNameFromEmail(auth.userEmail);
  const greeting = `${greetingForHour(new Date().getHours())}${name ? `, ${name}` : ''}`;

  return (
    <Container size="xl" px={0}>
      <Stack gap="xl">
        <PageHeader title={greeting} description={auth.activeTenant?.label ?? 'No tenant selected'} />

        <SiteStatusStrip />

        <Stack gap="sm">
          <Text fw={600} size="sm" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.04em' }}>
            Tasks
          </Text>
          {tasks.length > 0 ? (
            <>
              <style>{TASK_CARD_STYLES}</style>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
                {tasks.map((task) => (
                  <TaskButton
                    key={task.label}
                    label={task.label}
                    to={task.to}
                    icon={task.icon}
                    description={task.description}
                    color={TASK_COLORS[task.label]}
                  />
                ))}
              </SimpleGrid>
            </>
          ) : (
            <Text c="dimmed">Your role has no quick tasks here — use the reports and search in the nav.</Text>
          )}
        </Stack>
      </Stack>
    </Container>
  );
}
